import React from 'react';
import { Link } from 'react-router-dom';
import { Music, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface EnhancedCategoryCardProps {
  id: string;
  slug: string;
  title: string;
  description: string;
  platform: string;
  sequenceCount: number;
  isFeatured?: boolean;
  imageUrl: string | null;
  imageAlt?: string | null;
  gradientFrom?: string;
  gradientTo?: string;
}

export const EnhancedCategoryCard: React.FC<EnhancedCategoryCardProps> = ({
  id,
  slug,
  title,
  description,
  platform,
  sequenceCount,
  isFeatured = false,
  imageUrl,
  imageAlt,
  gradientFrom = 'from-blue-900/30',
  gradientTo = 'to-purple-900/30',
}) => {
  const platformColors = {
    'xLights': 'bg-blue-900/50 text-blue-200',
    'Light-O-Rama': 'bg-blue-900/50 text-blue-200',
    'Both': 'bg-gray-700 text-gray-300',
  };

  const platformColor = platformColors[platform as keyof typeof platformColors] || 'bg-gray-700 text-gray-300';

  return (
    <Link to={`/categories/${slug}`}>
      <div className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700 transition-all hover:transform hover:scale-[1.02] hover:shadow-xl hover:border-gray-600 cursor-pointer">
        {/* Image or Gradient Header */}
        <div className="relative h-72">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={imageAlt || title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className={`bg-linear-to-r ${gradientFrom} ${gradientTo} h-full flex items-center justify-between p-4`}>
              <h3 className="text-xl font-bold text-white">{title}</h3>
            </div>
          )}
          
          {/* Featured Badge */}
          {isFeatured && (
            <div className="absolute top-3 right-3 bg-blue-600 text-xs rounded-full px-2 py-1 flex items-center shadow-lg">
              <Star size={12} className="mr-1" />
              Featured
            </div>
          )}
        </div>

        {/* Card Content */}
        <div className="p-4">
          {/* Title - always show in content section now */}
          <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
          
          <p className="text-gray-300 mb-4 text-sm leading-relaxed">
            {description}
          </p>
          
          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center text-gray-400">
              <Music size={16} className="mr-2" />
              <span className="text-sm">
                {sequenceCount} {sequenceCount === 1 ? 'sequence' : 'sequences'}
              </span>
            </div>
            <Badge variant="outline" className={`text-xs ${platformColor} border-gray-600`}>
              {platform}
            </Badge>
          </div>
        </div>
      </div>
    </Link>
  );
};